import { AbstractAgent } from '@ag-ui/client';
import type { BaseEvent, RunAgentInput } from '@ag-ui/client';
import { Observable } from 'rxjs';
import { rpcClient } from '@/api/rpc/client';

/**
 * A real AG-UI client (AI-244 — see docs/operator/operator-live-stream.md).
 *
 * Per the AG-UI architecture the client lives on the frontend; its core
 * abstraction is `run(): Observable<BaseEvent>`. We extend AbstractAgent and
 * bridge the existing oRPC `subscribeOperatorSession` event iterator into that
 * observable — keeping the repo's oRPC transport while getting the canonical
 * client (which auto-reduces STATE_SNAPSHOT/STATE_DELTA into its managed state).
 */
export interface OperatorAgentConfig {
  threadId: string;
  sessionId: string;
  organizationId: string;
}

export class OperatorAgent extends AbstractAgent {
  private readonly sessionId: string;
  private readonly organizationId: string;

  constructor(config: OperatorAgentConfig) {
    super({ threadId: config.threadId });
    this.sessionId = config.sessionId;
    this.organizationId = config.organizationId;
  }

  run(input: RunAgentInput): Observable<BaseEvent> {
    return new Observable<BaseEvent>((subscriber) => {
      const controller = new AbortController();

      void (async () => {
        try {
          const iterator = await rpcClient.agent.subscribeOperatorSession(
            {
              organizationId: this.organizationId,
              sessionId: this.sessionId,
              runId: input.runId,
              threadId: input.threadId,
            },
            { signal: controller.signal },
          );
          for await (const event of iterator) {
            // Canonical AG-UI events from the contract (agent.contract.ts); the
            // `type` literals are kept in lockstep with @ag-ui/core's EventType.
            subscriber.next(event as unknown as BaseEvent);
          }
          subscriber.complete();
        } catch (err) {
          subscriber.error(err);
        }
      })();

      return () => controller.abort();
    });
  }
}
